import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { eq, gt } from 'ember-truth-helpers';
import {
  HdsPageHeader,
  HdsDropdown,
  HdsTable,
  HdsButton,
  HdsApplicationState,
  HdsModal,
  HdsTextBody,
  HdsAlert,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  CoreUserAgentDisplay,
  Time,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import { GEOS } from 'core/utils/constants';

const { EU, US } = GEOS;

/**
 *
 * `PageAccountSettingsSessionManager` description here.
 *
 *
 * ```
 * <Page::AccountSettings::SessionManager
 *   @testArg={{foo}}
 * />
 * ```
 *
 * @class PageAccountSettingsSessionManager
 *
 */
export default class PageAccountSettingsSessionManager extends Component {
  @service api;
  @service config;
  @service currentUser;
  @service router;

  @tracked showRevokeSessionsModal = false;

  toggleRevokeSessionsModal = () => {
    this.showRevokeSessionsModal = !this.showRevokeSessionsModal;
  };

  revokeAllSessions = task(async () => {
    // prevents test failure in acceptance test
    if (this.isDestroyed || this.isDestroying) return;

    this.showRevokeSessionsModal = false;
    await this.api.sessionManager.sessionServiceRevokeMySessions();
    this.router.transitionTo('sign-out');
  });

  // revokeSession revokes a specific session and then refreshes the list.
  revokeSession = task(async (sid) => {
    // call the endpoint
    await this.api.sessionManager.sessionServiceRevokeSessionByID(sid);

    // refresh the model
    let route = getOwner(this).lookup(`route:${this.router.currentRouteName}`);
    route.refresh();
  });

  EU = EU;

  get deployGeo() {
    return this.config.app?.deployGeo ?? US;
  }

  <template>
    <div class="page-account-settings-session-manager">
      {{#let (eq this.deployGeo this.EU) as |isEU|}}
        <CoreLayoutMain>
          <:header>
            <HdsPageHeader as |PH|>
              <PH.Breadcrumb>
                <BreadcrumbsContainer />
              </PH.Breadcrumb>
              <PH.Title>
                {{t "components.page.account-settings.nav.session-manager"}}
              </PH.Title>
              <PH.IconTile @icon="user" />
              {{#if @model.sessions.length}}
                <PH.Actions>
                  <HdsDropdown @listPosition="bottom-right" as |DD|>
                    <DD.ToggleButton
                      @color="secondary"
                      @text={{t "components.form.manage"}}
                      data-test-more-menu-trigger
                    />
                    <DD.Interactive
                      @color="critical"
                      @icon="minus-circle"
                      @text={{t
                        "components.page.account-settings.session-manager.manage"
                      }}
                      {{on "click" this.toggleRevokeSessionsModal}}
                      data-test-revoke-sessions
                    />
                  </HdsDropdown>
                </PH.Actions>
              {{/if}}
            </HdsPageHeader>
          </:header>
          <:body>
            {{#if @model.sessions.length}}
              <HdsTable @valign="middle">
                <:head as |H|>
                  <H.Tr>
                    <H.Th>
                      {{t
                        "components.page.account-settings.session-manager.table.columns.user-agent"
                      }}
                    </H.Th>
                    <H.Th>
                      {{t
                        "components.page.account-settings.session-manager.table.columns.ip-address"
                      }}
                    </H.Th>
                    <H.Th>
                      {{t
                        "components.page.account-settings.session-manager.table.columns.created"
                      }}
                    </H.Th>
                    <H.Th>
                      {{t
                        "components.page.account-settings.session-manager.table.columns.last-seen"
                      }}
                    </H.Th>
                    {{#if (gt @model.sessions.length 1)}}
                      <H.Th>&nbsp;</H.Th>
                    {{/if}}
                  </H.Tr>
                </:head>
                <:body as |B|>
                  {{#each @model.sessions as |item index|}}
                    <B.Tr>
                      <B.Td>
                        <CoreUserAgentDisplay @userAgent={{item.userAgent}} />
                      </B.Td>
                      <B.Td>
                        {{item.ipAddress}}
                      </B.Td>
                      <B.Td>
                        <Time
                          @date={{item.createdAt}}
                          @display="friendly-local"
                        />
                      </B.Td>
                      <B.Td>
                        {{#if item.current}}
                          {{t
                            "components.page.account-settings.session-manager.table.sessions.current"
                          }}
                        {{else}}
                          <Time
                            @date={{item.lastSeen}}
                            @display="friendly-local"
                          />
                        {{/if}}
                      </B.Td>
                      {{#if (gt @model.sessions.length 1)}}
                        <B.Td>
                          {{#unless item.current}}
                            <HdsButton
                              data-test-revoke-single-session={{index}}
                              @color="critical"
                              @icon="minus-circle"
                              @text={{t
                                "components.page.account-settings.session-manager.table.sessions.revoke"
                              }}
                              disabled={{this.revokeSession.isRunning}}
                              type="button"
                              {{on
                                "click"
                                (fn this.revokeSession.perform item.id)
                              }}
                            />
                          {{/unless}}
                        </B.Td>
                      {{/if}}
                    </B.Tr>
                  {{/each}}
                </:body>
              </HdsTable>
            {{else}}
              <div {{boxPadding "2xl 0 0 0"}}>
                <HdsApplicationState data-test-revoke-sessions-empty as |A|>
                  <A.Header
                    @title={{t
                      "components.page.account-settings.session-manager.empty-state.title"
                    }}
                  />
                  <A.Body
                    @text={{t
                      "components.page.account-settings.session-manager.empty-state.description"
                    }}
                  />
                </HdsApplicationState>
              </div>
            {{/if}}

            {{#if this.showRevokeSessionsModal}}
              <HdsModal
                @color="critical"
                @onClose={{this.toggleRevokeSessionsModal}}
                data-test-revoke-sessions-modal
                as |M|
              >
                <M.Header @icon="alert-diamond">
                  {{#if isEU}}
                    {{t
                      "components.page.account-settings.session-manager.revoke-modal.eu.title"
                    }}
                  {{else}}
                    {{t
                      "components.page.account-settings.session-manager.revoke-modal.title"
                    }}
                  {{/if}}
                </M.Header>
                <M.Body>
                  {{#if isEU}}
                    <HdsTextBody @color="primary" @size="200" @tag="p">
                      {{t
                        "components.page.account-settings.session-manager.revoke-modal.eu.body"
                        htmlSafe=true
                        email=this.currentUser.user.email
                      }}
                    </HdsTextBody>
                  {{else}}
                    <HdsTextBody @color="primary" @size="200" @tag="p">
                      {{t
                        "components.page.account-settings.session-manager.revoke-modal.body"
                        htmlSafe=true
                        email=this.currentUser.user.email
                      }}
                    </HdsTextBody>
                    <HdsAlert
                      data-test-revoke-session-alert
                      @type="inline"
                      @color="neutral"
                      as |A|
                    >
                      <A.Description>
                        {{t
                          "components.page.account-settings.session-manager.revoke-modal.info"
                        }}
                      </A.Description>
                    </HdsAlert>
                  {{/if}}
                </M.Body>
                <M.Footer as |F|>
                  <HdsButtonSet>
                    <HdsButton
                      data-test-revoke
                      @color="critical"
                      @text={{t
                        "components.page.account-settings.session-manager.revoke-modal.button"
                      }}
                      disabled={{this.revokeAllSessions.isRunning}}
                      {{on "click" this.revokeAllSessions.perform}}
                    />
                    <HdsButton
                      @color="secondary"
                      @text={{t "components.form.cancel"}}
                      {{on "click" F.close}}
                    />
                  </HdsButtonSet>
                </M.Footer>
              </HdsModal>
            {{/if}}
          </:body>
        </CoreLayoutMain>
      {{/let}}
    </div>
  </template>
}
